import { authService } from 'partner-oidc-auth'
import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { HttpService } from './gateway'
import { IServiceOptions } from './types'
import { createObjectSingleton } from '../../utils/singletone'
import { handleResponseError, ApiAlertContext } from './helpers'

export interface ITokenProvider {
  getToken: () => Promise<string | undefined>
  reSignIn: () => Promise<void>
  getRoles: () => Promise<string[] | undefined>
}

class TokenProviderDefault implements ITokenProvider {
  async getToken(): Promise<string | undefined> {
    return (await authService.getAccessToken()) || undefined
  }
  async reSignIn() {
    await authService.signIn({})
  }
  async getRoles(): Promise<string[] | undefined> {
    const roles = await authService.getRoles()
    return roles || undefined
  }
}

/**
 * Base service class for HTTP operations with authentication and error handling
 * Provides common CRUD operations with automatic token management and retry logic
 */
export default class ServiceBase {
  static key_token_provider = '__t1_22zzz_t_pro_key__'
  static get tokenProvider(): ITokenProvider {
    const ObjectProvider = createObjectSingleton<ITokenProvider>(ServiceBase.key_token_provider)
    return ObjectProvider.get() ?? new TokenProviderDefault()
  }
  static setTokenProvider(provider: ITokenProvider) {
    const ObjectProvider = createObjectSingleton<ITokenProvider>(ServiceBase.key_token_provider)
    ObjectProvider.set(provider ?? new TokenProviderDefault())
  }

  private _http: AxiosInstance
  private options?: IServiceOptions
  constructor(http?: AxiosInstance, options?: IServiceOptions) {
    this.options = options
    this._http = http || HttpService
    this.mapResponse()
  }
  /**
   * Maps response and request interceptors for handling authentication and errors
   * Automatically adds authorization headers and retries requests on 401 errors
   */
  mapResponse = () => {
    this._http.interceptors.response.use(
      (res) => res,
      (err) => this.tryFetchToken(err, handleResponseError)
    )
    this._http.interceptors.request.use((req) => {
      return this.interuptHeader(req)
    })
  }
  /**
   * Tries to fetch a new token if the current one is expired
   * @param error - The axios error containing response data
   * @param next - The next middleware function to call
   * @returns A promise resolving to the response data or an error
   */
  tryFetchToken = async (error: AxiosError, next: (error: AxiosError) => Promise<any>): Promise<any> => {
    const originalRequest = error.config
    if (error?.response?.status === 401 && originalRequest) {
      await ServiceBase.tokenProvider.reSignIn()
      await this.interuptHeader(originalRequest)
      return axios(originalRequest)
    }
    return next(error)
  }
  private _token?: string
  /**
   * Sets the token to be used for authentication
   * @param token - The token string to set
   */
  setToken(token?: string) {
    this._token = token
  }
  /**
   * Gets the current token, fetching a new one if not set
   * @returns A promise resolving to the token string
   */
  static getApiKeyFromQuery(): string | null {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    const akp = params.get('akp')
    return akp
  }
  getToken = async () => {
    if (this.options?.permission === 'guest') return undefined
    return this._token === undefined ? await ServiceBase.tokenProvider.getToken() : this._token
  }
  /**
   * Adds custom headers to the request configuration
   * @param config - The Axios request configuration object
   * @returns A promise resolving to the updated configuration object
   */
  async addCustomHeader(config?: AxiosRequestConfig) {
    if (!config) {
      config = { headers: {} }
    }
    if (!config.headers) {
      config.headers = {}
    }
    return config
  }
  /**
   * Intercepts the request to add the authorization token
   * @param config - The Axios request configuration object
   * @returns The updated configuration object with the token added
   */
  private async interuptHeader(config: InternalAxiosRequestConfig) {
    await this.addtoken(config)
    return config
  }
  /**
   * Adds the authorization token to the request headers
   * @param config - The Axios request configuration object
   * @returns The updated configuration object with the token added
   */
  private addtoken = async (config?: AxiosRequestConfig) => {
    const akp = ServiceBase.getApiKeyFromQuery()
    if (akp && config?.headers) {
      config.headers['X-API-KEY'] = akp
      return config
    }
    const _token = await this.getToken()
    if (_token && config?.headers) {
      config.headers.Authorization = 'Bearer ' + _token
    }
    return config
  }
  /**
   * HTTP GET request with automatic authentication
   * @param url - The request URL
   * @param config - Optional axios configuration
   * @returns Promise with the response data
   */
  async get<TModel>(url: string, config?: AxiosRequestConfig | undefined) {
    const response = await this._http.get<TModel>(url, await this.addCustomHeader(config))
    return response.data
  }
  /**
   * HTTP GET request with error handling
   * @param url - The request URL
   * @param config - Optional axios configuration
   * @returns Promise with the response data or null if an error occurs
   */
  async tryGet<TModel>(url: string, config?: AxiosRequestConfig | undefined) {
    try {
      return this.get<TModel>(url, config)
    } catch {
      return null
    }
  }
  /**
   * HTTP POST request with automatic authentication
   * @param url - The request URL
   * @param data - Optional data to send in the request body
   * @param config - Optional axios configuration
   * @returns Promise with the response data
   */
  async post<TModel>(url: string, data?: any, config?: AxiosRequestConfig | undefined) {
    const response = await this._http.post<TModel>(url, data, await this.addCustomHeader(config))
    return response.data
  }
  /**
   * HTTP PUT request with automatic authentication
   * @param url - The request URL
   * @param data - Optional data to send in the request body
   * @param config - Optional axios configuration
   * @returns Promise with the response data
   */
  async put<TModel>(url: string, data?: any, config?: AxiosRequestConfig | undefined) {
    const response = await this._http.put<TModel>(url, data, await this.addCustomHeader(config))
    return response.data
  }
  /**
   * HTTP PUT request with error handling
   * @param url - The request URL
   * @param data - Optional data to send in the request body
   * @param config - Optional axios configuration
   * @returns Promise with the response data or null if an error occurs
   */
  async tryPut<TModel>(url: string, data?: any, config?: AxiosRequestConfig | undefined) {
    try {
      await this.addCustomHeader(config)
      return await this.put<TModel>(url, data, config)
    } catch (error) {
      // processError(error as any)
      return null
    }
  }
  /**
   * HTTP PATCH request with automatic authentication
   * @param url - The request URL
   * @param data - Optional data to send in the request body
   * @param config - Optional axios configuration
   * @returns Promise with the response data
   */
  async patch<TModel>(url: string, data?: any, config?: AxiosRequestConfig | undefined) {
    const response = await this._http.patch<TModel>(url, data, await this.addCustomHeader(config))
    return response.data
  }
  /**
   * HTTP PATCH request with error handling
   * @param url - The request URL
   * @param data - Optional data to send in the request body
   * @param config - Optional axios configuration
   * @returns Promise with the response data or null if an error occurs
   */
  async tryPatch<TModel>(url: string, data?: any, config?: AxiosRequestConfig | undefined) {
    try {
      await this.addCustomHeader(config)
      return await this.patch<TModel>(url, data, config)
    } catch (error) {
      // processError(error as any)
      return null
    }
  }
  /**
   * HTTP POST request with error handling
   * @param url - The request URL
   * @param data - Optional data to send in the request body
   * @param config - Optional axios configuration
   * @returns Promise with the response data or null if an error occurs
   */
  async tryPost<TModel>(url: string, data?: any, config?: AxiosRequestConfig | undefined) {
    try {
      return await this.post<TModel>(url, data, config)
    } catch (error) {
      // processError(error as any)
      return null
    }
  }
  /**
   * HTTP DELETE request with automatic authentication
   * @param url - The request URL
   * @param config - Optional axios configuration
   * @returns Promise with the response data
   */
  async delete<TModel>(url: string, config?: AxiosRequestConfig | undefined) {
    const response = await this._http.delete<TModel>(url, await this.addCustomHeader(config))
    return response.data
  }
  /**
   * HTTP DELETE request with error handling
   * @param url - The request URL
   * @param config - Optional axios configuration
   * @returns Promise with the response data or null if an error occurs
   */
  async tryDelete<TModel>(url: string, config?: AxiosRequestConfig | undefined) {
    try {
      return await this.delete<TModel>(url, await this.addCustomHeader(config))
    } catch (error) {
      // processError(error as any)
      return null
    }
  }
  /**
   * Push notification with error handling
   * @param action - The function to execute
   * @param p - Parameters to pass to the function
   * @returns Promise with the result of the function execution
   */
  async tryPushNotify<TFunc extends (...param: any[]) => any>(action: TFunc, ...p: Parameters<TFunc>) {
    try {
      return await this.pushNotify(action, ...p)
    } catch (err) {}
  }
  /**
   * Push notification with error handling
   * @param action - The function to execute
   * @param p - Parameters to pass to the function
   * @returns Promise with the result of the function execution
   */
  async pushNotify<TFunc extends (...param: any[]) => any>(action: TFunc, ...p: Parameters<TFunc>): Promise<ReturnType<TFunc>> {
    try {
      return await action.bind(this)(...p)
    } catch (err) {
      const error = err as AxiosError
      const data = error.response?.data as any
      if (data) {
        const keys = Object.keys(data)
        keys.forEach((key) => {
          const errors = data[key] as string[]
          if (Array.isArray(errors)) {
            errors.forEach((message) => {
              ApiAlertContext?.ApiAlert?.PushError(message)
            })
          }
        })
      }
      throw error
    }
  }
}
/**
 * CancelAction class extends AbortController to provide a cancel method
 * This class can be used to cancel ongoing HTTP requests
 */
export class CancelAction extends AbortController {
  cancel = () => {
    this.trigger && this.trigger()
    super.abort()
  }
  trigger?: () => void
}
